/* SPDX-FileCopyrightText: Copyright (c) 2026 NVIDIA CORPORATION & AFFILIATES. All rights reserved. */
/* SPDX-License-Identifier: Apache-2.0 */

:host {
  --border-radius: var(--nve-ref-border-radius-xs);
  --border-background: var(--nve-sys-accent-secondary-background);
  --font-size: var(--nve-ref-font-size-100);
  --font-weight: var(--nve-ref-font-weight-medium);
  --color: var(--nve-sys-interaction-color);
  --padding: var(--nve-ref-size-200);
  --gap: var(--nve-ref-space-sm);
  --width: 100%;
  --min-height: var(--nve-ref-size-800);
  --line-height: 0;
  --cursor: pointer;
  --text-transform: capitalize;
  --opacity: 1;
  contain: layout;
  content-visibility: auto;
  contain-intrinsic-size: auto var(--min-height, initial);
}

[internal-host] {
  --_icon-color: var(--color);
  padding: var(--padding);
  color: var(--color);
  font-size: var(--font-size);
  font-weight: var(--font-weight);
  border-radius: var(--border-radius);
  gap: var(--gap);
  width: var(--width);
  min-width: var(--min-width);
  min-height: var(--min-height);
  cursor: var(--cursor);
  text-transform: var(--text-transform);
  outline-offset: calc(-1 * var(--nve-ref-size-50));
  opacity: var(--opacity);
  position: relative;
  display: flex;
  align-items: center;
  white-space: nowrap;
  border: 0;
  text-align: left;
}

::slotted(nve-icon) {
  --color: var(--_icon-color);
}

:host(:hover),
:host(:state(selected)) {
  --color: var(--nve-sys-interaction-selected-color);
}

:host(:state(disabled)) [interaction-state] {
  --nve-sys-interaction-state-ratio: 0;
}

:host(:state(disabled)) {
  --opacity: var(--nve-ref-opacity-500);
  --cursor: not-allowed;
}

:host(:state(current)) {
  contain: none;
  content-visibility: initial;
  --color: var(--nve-sys-interaction-selected-color);
}

:host(:state(current)) [internal-host] {
  contain: none;
}

:host([status='danger']) {
  --nve-sys-interaction-state-base: var(--nve-sys-support-danger-muted-color);
  --nve-sys-interaction-selected-color: var(--nve-sys-support-danger-emphasis-color);
  --border-background: var(--nve-sys-support-danger-emphasis-color);
  --color: var(--nve-sys-support-danger-emphasis-color);
}

:host([status='danger']:hover) ::slotted(nve-icon) {
  --color: var(--nve-sys-support-danger-emphasis-color) !important;
}

:host(:state(current)) [internal-host]::after {
  width: var(--nve-ref-border-width-xl);
  border-radius: var(--border-radius);
  background: var(--border-background);
  content: '';
  display: block;
  position: absolute;
  top: var(--nve-ref-size-50);
  bottom: var(--nve-ref-size-50);
  left: calc(var(--nve-ref-size-200) * -1);
}

::slotted(nve-icon) {
  --color: inherit !important;
}

::slotted(a) {
  text-decoration: var(--text-decoration) !important;
  color: var(--color) !important;
  gap: var(--gap) !important;
  display: flex !important;
  align-items: center !important;
  outline: 0;
}

::slotted(a)::after {
  display: block;
  content: ' ';
  inset: 0;
  position: absolute;
}

::slotted(*:not(a, nve-icon)) {
  pointer-events: none;
}

::slotted(nve-divider) {
  margin: 0 calc(-1 * var(--padding));
}

::slotted([slot='suffix']) {
  margin-inline-start: auto;
}
